import { useAppNavigate, useCharacterCommissions } from '@sorrem-client/ui/hooks';
import { forgetPostLoginPath } from '@sorrem-client/ui/shared/post-login-path';
import { useCallback, useEffect, useState } from 'react';
import type { CrucibleCreateDraft } from '../tabs/CruciblesTab';
import { useCrucibleCatalog, type CatalogCrucible } from './use-crucible-catalog';
import { useCrucibleInvite, useCrucibleInviteCode } from './use-crucible-invite';

export interface CrucibleSelection {
    crucibles: readonly CatalogCrucible[];
    /** Whether more open crucibles can be loaded onto the list. */
    hasMoreCrucibles: boolean;
    isLoadingMoreCrucibles: boolean;
    showMoreCrucibles: () => void;
    /** Until the first commission is complete, the Crucibles tab shows only a hint. */
    isCruciblesLocked: boolean;
    /** The crucible Crucible Info shows, or null. */
    selectedCrucible: CatalogCrucible | null;
    /** Why a linked crucible can't be joined (or that an invite code names none), or null. */
    unavailableNotice: string | null;
    /** While the create form is open, Crucible Info previews what it would make (#224). */
    createDraft: CrucibleCreateDraft | null;
    setCreateDraft: (draft: CrucibleCreateDraft | null) => void;
    /** Selects a crucible by navigating to its link. */
    selectCrucible: (crucibleId: string) => void;
}

/**
 * The crucible selected on the Crucibles tab. Its main-panel list and
 * side-panel details are separate panels in different regions of the
 * screen, so the selection they share lives here instead of inside either
 * one. `selectedId` is the crucible the route links to, if any; an
 * `inviteCode` route links to the crucible the code names.
 */
export function useCrucibleSelection(selectedId: string | undefined, inviteCode: string | undefined): CrucibleSelection {
    const navigate = useAppNavigate();
    const { crucibles, hasMore, isLoadingMore, showMore } = useCrucibleCatalog();
    const { isCruciblesLocked } = useCharacterCommissions();
    const codeLookup = useCrucibleInviteCode(inviteCode ?? null);
    const routeCrucibleId = selectedId ?? codeLookup.crucibleId ?? undefined;
    // The crucible a route last linked to: kept when the route moves on without one (the plain Crucibles tab).
    const [selectedCrucibleId, setSelectedCrucibleId] = useState<string | null>(routeCrucibleId ?? null);
    if (routeCrucibleId && routeCrucibleId !== selectedCrucibleId) {
        setSelectedCrucibleId(routeCrucibleId);
    }
    const [createDraft, setCreateDraft] = useState<CrucibleCreateDraft | null>(null);

    // The invite a login brought the player back for is reached; it's followed once, even to a locked tab.
    const hasInviteRoute = selectedId !== undefined || inviteCode !== undefined;
    useEffect(() => {
        if (hasInviteRoute) {
            forgetPostLoginPath();
        }
    }, [hasInviteRoute]);

    // A link to one crucible (an invite, #216, or its code) shows that crucible,
    // or why it can't be joined; it never falls back to another crucible in the list.
    const shownCrucibleId = hasInviteRoute ? routeCrucibleId ?? null : selectedCrucibleId;
    const listedSelection = crucibles.find((crucible) => crucible.id === shownCrucibleId) ?? null;
    const linkNotice = useCrucibleInvite(selectedId ?? null, listedSelection !== null).unavailableNotice;
    const unavailableNotice = inviteCode !== undefined ? (listedSelection === null ? codeLookup.unavailableNotice : null) : linkNotice;
    const selectedCrucible = listedSelection ?? (hasInviteRoute ? null : crucibles[0] ?? null);

    const selectCrucible = useCallback((crucibleId: string): void => {
        navigate(`/character/crucibles/${encodeURIComponent(crucibleId)}`);
    }, [navigate]);

    return {
        crucibles,
        hasMoreCrucibles: hasMore,
        isLoadingMoreCrucibles: isLoadingMore,
        showMoreCrucibles: showMore,
        isCruciblesLocked,
        selectedCrucible,
        unavailableNotice,
        createDraft,
        setCreateDraft,
        selectCrucible,
    };
}
